import { sortHand } from "../game/cards";
import { Rank, Suit, type Card, type CbTableState } from "../gen/bazivan/v1/table_pb";
import { placeLabel } from "../game/place";
import { toLocal, toServer } from "../hokm/state";

export { toLocal, toServer };

/**
 * دیدِ محلیِ میزِ چهاربرگ — پورتِ CharBargOnlineMap.
 *
 * همان دو قاعده‌ی میزِ حکم: چرخشِ صندلی تا خودت صندلیِ ۰ باشی، و دستِ حریف فقط
 * به تعداد (نما پشت‌ورو می‌کشد). دسته‌های جمع‌شده هم فقط شمارش‌اند، جز نشانِ سور
 * که کارتش را سرور می‌فرستد چون رو نشان داده می‌شود.
 */
export type CbLocal = {
  n: number; me: number;
  names: string[]; bots: boolean[]; avatars: string[]; subscribers: boolean[]; connected: boolean[]; playerIds: string[];
  /** برچسبِ جا (شهر یا کشور) — خالی اگر بازیکن نگفته یا نشان نداده. */
  places: string[];
  carpet: string;
  myHand: Card[];
  counts: number[];        // شمارِ دستِ هر صندلی (محلی)
  piles: number[];         // شمارِ جمع‌شده‌های هر صندلی (محلی)
  surCards: Card[][];      // نشانِ سورهای هر صندلی (محلی)
  table: Card[];
  turn: number;            // محلی؛ ‎-۱ سرِ پایانِ دور
  deckLeft: number;
  teams: boolean; units: number;
  score: number[];         // به واحدِ محلی
  targetScore: number;
  roundOver: boolean; matchOver: boolean; lastDeal: boolean;
  myTurn: boolean;
  awaitingCapture: boolean;
  capturePlayed: Card | null;
  captureOptions: Card[][];
  captureBest: number;
  turnRemaining: number;   // ثانیه؛ باقی‌مانده
  /**
   * نمای تماشاچی: هیچ دستی مالِ من نیست.
   *
   * سرور my_hand را خالی می‌فرستد و me فقط لنگرِ چرخشِ صندلی‌هاست (۰). پس
   * صندلیِ ۰ هم مثلِ بقیه پشت‌ورو کشیده می‌شود و هیچ نوبتی «نوبتِ من» نیست —
   * وگرنه تماشاچی ساعتِ نوبت و دستِ خالیِ روشن می‌دید و فکر می‌کرد باید بازی
   * کند.
   */
  watching: boolean;
};

export const unitOf = (st: CbLocal, seat: number) => (st.teams ? seat % 2 : seat);
export const toLocalUnit = (serverUnit: number, me: number, units: number) => (units <= 1 ? 0 : ((serverUnit - me) % units + units) % units);
export const isPrize = (c: Card) => (c.rank === Rank.TEN && c.suit === Suit.DIAMONDS) || (c.rank === Rank.TWO && c.suit === Suit.CLUBS);
export const isGoldTen = (c: Card) => c.rank === Rank.TEN && c.suit === Suit.DIAMONDS;
export const sameCard = (a: Card, b: Card) => a.suit === b.suit && a.rank === b.rank;
/** روی زمینِ خالی سرباز نمی‌اندازند — IsForbiddenPlay. */
export const forbidden = (st: CbLocal, c: Card) => st.table.length === 0 && c.rank === Rank.JACK;

export function emptyCb(): CbLocal {
  return {
    n: 4, me: 0, names: [], bots: [], avatars: [], subscribers: [], connected: [], playerIds: [], places: [], carpet: "",
    myHand: [], counts: [], piles: [], surCards: [], table: [], turn: -1, deckLeft: 0, teams: true, units: 2,
    score: [], targetScore: 0, roundOver: false, matchOver: false, lastDeal: false, myTurn: false,
    awaitingCapture: false, capturePlayed: null, captureOptions: [], captureBest: 0, turnRemaining: 0,
    watching: false,
  };
}

export function applyCb(ts: CbTableState): CbLocal {
  const n = ts.seats.length > 0 ? ts.seats.length : 4;
  const me = ts.me;
  const st = emptyCb();
  st.n = n; st.me = me;
  st.teams = n >= 4 && n % 2 === 0;
  st.units = st.teams ? 2 : n;
  const arr = <T,>(v: T) => new Array<T>(n).fill(v);
  st.names = arr(""); st.bots = arr(false); st.avatars = arr(""); st.subscribers = arr(false); st.connected = arr(true); st.playerIds = arr(""); st.places = arr("");
  for (const s of ts.seats) {
    const l = toLocal(s.index, me, n);
    if (l < 0 || l >= n) continue;
    st.names[l] = s.name; st.bots[l] = s.bot; st.avatars[l] = s.avatar ?? "";
    st.subscribers[l] = !!s.subscriber; st.connected[l] = s.connected || s.bot; st.playerIds[l] = s.playerId ?? "";
    st.places[l] = placeLabel(s);
  }
  st.carpet = ts.carpet ?? "";
  st.watching = ts.watching;
  // مرتب، مثلِ حکم. دستِ نامرتب روی گوشی یعنی بازیکن هر نوبت کلِ بادبزن را
  // با چشم جارو می‌کند تا خالش را پیدا کند.
  st.myHand = sortHand(ts.myHand);
  st.counts = arr(0); st.piles = arr(0); st.surCards = Array.from({ length: n }, () => []);
  for (let server = 0; server < n; server++) {
    const l = toLocal(server, me, n);
    st.counts[l] = l === 0 && !st.watching ? ts.myHand.length : (ts.handCounts[server] ?? 0);
    st.piles[l] = ts.pileCounts[server] ?? 0;
  }
  for (const m of ts.surMarks) {
    const l = toLocal(m.seat, me, n);
    if (l >= 0 && l < n && m.card) st.surCards[l]!.push(m.card);
  }
  st.table = [...ts.table];
  st.turn = ts.roundOver ? -1 : toLocal(ts.turn, me, n);
  st.deckLeft = ts.deckLeft;
  st.score = new Array(st.units).fill(0);
  for (let u = 0; u < st.units; u++) {
    const server = toServer(u, me, st.units);
    st.score[u] = ts.score[server] ?? 0;
  }
  st.targetScore = ts.targetScore;
  st.roundOver = ts.roundOver; st.matchOver = ts.matchOver; st.lastDeal = ts.lastDeal;
  st.awaitingCapture = ts.awaitingCapture;
  st.myTurn = !st.watching && !ts.roundOver && !ts.matchOver && st.turn === 0 && !ts.awaitingCapture;
  if (ts.awaitingCapture && ts.capturePlayed) {
    st.capturePlayed = ts.capturePlayed;
    st.captureOptions = ts.captureOptions.map((o) => [...o.cards]);
    st.captureBest = ts.captureBest;
  }
  st.turnRemaining = ts.turnDeadlineMs / 1000;
  return st;
}
